<template>
  <v-row id="techniqueDetails" v-if="selectedTechnique && selectedTechnique.id">
    <v-col cols="12">
      <h4 class="overline mb-1">Technique Details</h4>
      <v-card flat color="bg_darkerish">
        <v-card-text class="py-6 px-6">
          <p class="text-h6 font-weight-light white--text">
            <span class="dw_green--text mr-2">{{ selectedTechnique.mitre_id }}</span>
            {{ selectedTechnique.name }}
            <v-chip
              label
              outlined
              x-small
              color="grey"
              class="ml-2 text-uppercase">{{ selectedTechnique.matrix_type }}</v-chip>
            <v-chip
              v-if="selectedTechnique.revoked"
              label
              outlined
              x-small
              color="red accent-2"
              class="ml-2 text-uppercase">Revoked</v-chip>
            <v-chip
              v-if="selectedTechnique.deprecated"
              label
              outlined
              x-small
              color="red accent-2"
              class="ml-2 text-uppercase">Deprecated</v-chip>
          </p>
          <p class="body-2 font-weight-light">
            {{ selectedTechnique.description }}
          </p>
          <v-divider class="my-2"></v-divider>

          <div v-if="selectedTechnique.parent">
            <p class="text-subtitle-1 font-weight-regular white--text mb-0">
              <span>MITRE ATT&amp;CK&reg; Parent Technique</span>
            </p>
            <v-chip
              label
              outlined
              small
              color="dw_green"
              class="my-2"
              @click="$router.push({
                name: 'technique_view',
                params: { id: selectedTechnique.parent.id } })"
            >
              {{ selectedTechnique.parent.mitre_id }}
            </v-chip>
            <v-divider class="my-2"></v-divider>
          </div>

          <div v-if="selectedTechnique.subtechniques.length > 0">
            <p class="text-subtitle-1 font-weight-regular white--text mb-0">
              <span>MITRE ATT&amp;CK&reg; Sub-Techniques</span>
            </p>
            <v-chip-group column>
              <v-chip
                v-for="subteq in selectedTechnique.subtechniques"
                :key="`subteq.${subteq.id}`"
                label
                outlined
                small
                :color="(subteq.revoked || subteq.deprecated) ? 'red' : 'dw_green'"
                @click="$router.push({ name: 'technique_view', params: { id: subteq.id } })"
              >
                {{ subteq.mitre_id }}
              </v-chip>
            </v-chip-group>
            <v-divider class="my-2"></v-divider>
          </div>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            <span>MITRE ATT&amp;CK&reg; Tactics</span>
          </p>
          <v-chip-group column>
            <v-chip
              v-if="selectedTechnique.tactics.length === 0"
              label
              outlined
              small
              color="grey">No Associated Tactics</v-chip>
            <v-chip
              v-else
              v-for="tac in selectedTechnique.tactics"
              :key="`tac.${tac.id}`"
              label
              outlined
              small
              :color="(tac.revoked || tac.deprecated) ? 'red' : 'dw_green'"
              @click="selectTactic(tac)">{{ tac.mitre_id }} - {{ tac.name }}</v-chip>
          </v-chip-group>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Mitigations
          </p>
          <v-chip-group column>
            <v-chip
              v-if="selectedTechnique.mitigations.length === 0"
              label
              outlined
              small
              color="grey">No Associated Mitigations</v-chip>
            <v-chip
              v-else
              v-for="mit in selectedTechnique.mitigations"
              :key="`mit.${mit.id}`"
              label
              outlined
              small
              color="dw_green"
              @click="selectMitigation(mit)">{{ mit.name }}</v-chip>
          </v-chip-group>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Platforms
          </p>
          <li v-if="selectedTechnique.platforms.length === 0">No Associated Platforms</li>
          <li v-else v-for="plat in selectedTechnique.platforms" :key="`plat.${plat.id}`">
            <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
            {{ plat.name }}
              <v-btn
                icon
                small
                v-if="!filters.pltfms.some((fltr) => fltr.id === plat.id)"
                @click="$emit('filter-by-platform', 'add', plat);"
                class="mb-1 ml-2"
                title="Add Filter">
                <v-icon small color="dw_green" v-text="mdiFilterVariantPlus" />
              </v-btn>
              <v-btn
                icon
                small
                v-else
                @click="$emit('filter-by-platform', 'remove', plat);"
                class="mb-1 ml-2"
                title="Remove Filter">
                <v-icon small color="danger" v-text="mdiFilterVariantRemove" />
              </v-btn>
          </li>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Software
          </p>
          <v-chip-group column>
            <v-chip
              v-if="selectedTechnique.software_procedures.length === 0"
              label
              outlined
              small
              color="grey">No Associated Software Procedures</v-chip>
            <v-chip
              v-else
              v-for="soft in selectedTechnique.software_procedures"
              :key="`soft.${soft.id}`"
              label
              outlined
              small
              color="dw_green"
              @click="selectSoftware(soft)">{{ soft.software }}</v-chip>
          </v-chip-group>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Groups
          </p>
          <v-chip-group column>
            <v-chip
              v-if="selectedTechnique.group_procedures.length === 0"
              label
              outlined
              small
              color="grey">No Associated Group Procedures</v-chip>
            <v-chip
              v-else
              v-for="grp in selectedTechnique.group_procedures"
              :key="`grp.${grp.id}`"
              label
              outlined
              small
              color="dw_green"
              @click="selectGroup(grp)">{{ grp.group }}</v-chip>
          </v-chip-group>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            <span>Related Plays</span>
          </p>
          <v-chip-group column>
            <v-chip
              v-if="selectedTechnique.related_plays.length === 0"
              label
              outlined
              small
              color="grey">No Related Plays</v-chip>
            <v-tooltip
              bottom
              v-else
              v-for="ply in selectedTechnique.related_plays"
              :key="`ply.${ply.id}`">
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  label
                  outlined
                  small
                  color="dw_green"
                  v-bind="attrs"
                  v-on="on"
                  @click="$router.push({ name: 'play_view', params: { id: ply.id }})">
                  {{ ply.id }}
                </v-chip>
              </template>
              <span>{{ ply.name }}</span>
            </v-tooltip>
          </v-chip-group>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            <span>Related NIST SP 800-53 Controls</span>
          </p>
          <v-chip-group column>
            <v-chip
              v-if="selectedTechnique.related_controls.length === 0"
              label
              outlined
              small
              color="grey">No Associated NIST SP 800-53 Controls</v-chip>
            <v-chip
              v-else
              v-for="ctrl in selectedTechnique.related_controls"
              :key="`ctrl.${ctrl.sort_id}`"
              label
              outlined
              small
              color="dw_green"
              @click="$router.push({ name: 'control_view', params: { id: ctrl.id } })">
              {{ ctrl.label }}
            </v-chip>
          </v-chip-group>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col cols="12" id="tacticDetails">
      <div v-if="selectedTactic"><!-- extra div for scrolling -->
        <h4 class="overline mb-1">Tactic Details</h4>
        <v-card flat color="bg_darkerish" min-height="20vh">
          <tactic-details :tactic="selectedTactic" />
          <v-system-bar height="4"></v-system-bar>
          <v-card-actions>
            <v-slide-group multiple show-arrows>
              <v-slide-item v-for="tac in selectedTechnique.tactics" :key="`tac.${tac.id}`">
                <v-btn text @click="selectTactic(tac)">{{ tac.mitre_id }}</v-btn>
              </v-slide-item>
            </v-slide-group>
          </v-card-actions>
        </v-card>
      </div>
    </v-col>

    <v-col cols="12" id="mitigationDetails">
      <div v-if="selectedMitigation"><!-- extra div for scrolling -->
        <h4 class="overline mb-1">Mitigation Details</h4>
        <v-card flat color="bg_darkerish" min-height="20vh">
          <mitigation-details :mitigation="selectedMitigation" />
          <v-system-bar height="4"></v-system-bar>
          <v-card-actions>
            <v-slide-group multiple show-arrows>
              <v-slide-item v-for="mit in selectedTechnique.mitigations"
                :key="`mit.${mit.id}`">
                <v-btn text @click="selectMitigation(mit)">{{ mit.name }}</v-btn>
              </v-slide-item>
            </v-slide-group>
          </v-card-actions>
        </v-card>
      </div>
    </v-col>

    <v-col cols="12" id="softwareDetails">
      <div v-if="selectedSoftware"><!-- extra div for scrolling -->
        <h4 class="overline mb-1">Software Details</h4>
        <v-card flat color="bg_darkerish" min-height="20vh">
          <software-details :software="selectedSoftware" />
          <v-system-bar height="4"></v-system-bar>
          <v-card-actions>
            <v-slide-group multiple show-arrows>
              <v-slide-item v-for="soft in selectedTechnique.software_procedures"
                :key="`soft.${soft.id}`">
                <v-btn text @click="selectSoftware(soft)">{{ soft.software }}</v-btn>
              </v-slide-item>
            </v-slide-group>
          </v-card-actions>
        </v-card>
      </div>
    </v-col>

    <v-col cols="12" id="groupsDetails">
      <div v-if="selectedGroup"><!-- extra div for scrolling -->
        <h4 class="overline mb-1">Group Details</h4>
        <v-card flat color="bg_darkerish" min-height="20vh">
          <group-details :group="selectedGroup" />
          <v-system-bar height="4"></v-system-bar>
          <v-card-actions>
            <v-slide-group multiple show-arrows>
              <v-slide-item
                v-for="grp in selectedTechnique.group_procedures"
                :key="`grp.${grp.id}`"
              >
                <v-btn text @click="selectGroup(grp)">{{ grp.group }}</v-btn>
              </v-slide-item>
            </v-slide-group>
          </v-card-actions>
        </v-card>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { mdiSquareMediumOutline, mdiFilterVariantPlus, mdiFilterVariantRemove } from '@mdi/js';
import apiService from '@/services/api.service';
import tacticDetails from '@/components/playbook/details/tacticDetails.vue';
import mitigationDetails from '@/components/playbook/details/mitigationDetails.vue';
import softwareDetails from '@/components/playbook/details/softwareDetails.vue';
import groupDetails from '@/components/playbook/details/groupDetails.vue';

export default {
  name: 'techniqueDetails',
  props: ['filters'],
  components: {
    tacticDetails,
    mitigationDetails,
    softwareDetails,
    groupDetails
  },
  data() {
    return {
      mdiSquareMediumOutline,
      mdiFilterVariantPlus,
      mdiFilterVariantRemove,
      selectedTactic: undefined,
      selectedMitigation: undefined,
      selectedSoftware: undefined,
      selectedGroup: undefined
    }
  },
  watch: {
    '$route.params': {
      async handler(params) {
        if (params.id) {
          await this.fetchTechniqueByID(params.id);
          this.clearSelectedDetails();
          this.$vuetify.goTo('#techniqueDetails');
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters([
      'selectedTechnique'
    ])
  },
  beforeRouteLeave(to, from, next) {
    if (to.name !== 'technique_view') {
      this.clearSelected('technique');
      this.clearSelectedDetails();
    }
    next();
  },
  methods: {
    ...mapActions([
      'fetchTechniqueByID'
    ]),
    ...mapMutations([
      'clearSelected',
      'throwError'
    ]),
    async getDetails(type, id) {
      const notFoundErrorMessage = `Unable to load ${type} information. If this problem persists, please contact an administrator.`;
      try {
        const data = await apiService.get(`${type}/${id}`);
        this.$vuetify.goTo(`#${type}Details`);
        return data;
      } catch {
        this.throwError(notFoundErrorMessage);
        this.$vuetify.goTo('#apiError');
        return {};
      }
    },
    selectTactic(tac) {
      // api call unnecessary, serializer already returns all fields
      this.selectedTactic = { ...tac };
      this.$vuetify.goTo('#tacticDetails');
    },
    selectMitigation(mit) {
      // api call unnecessary, serializer already returns all fields
      this.selectedMitigation = { ...mit };
      this.$vuetify.goTo('#mitigationDetails');
    },
    async selectSoftware(soft) {
      this.selectedSoftware = await this.getDetails('software', soft.software_id);
    },
    async selectGroup(grp) {
      this.selectedGroup = await this.getDetails('groups', grp.group_id);
    },
    clearSelectedDetails() {
      this.selectedTactic = undefined;
      this.selectedMitigation = undefined;
      this.selectedSoftware = undefined;
      this.selectedGroup = undefined;
    }
  }
};
</script>

<style scoped>
ul {
  padding: 0 0 0 0;
}
li {
  list-style-type: none;
}
</style>
